import type { UniverseSearchModel } from '../UniverseCreationModel';
import { PresetSelectionModel } from './PresetSelectionModel';
import type { PresetSelectionModelInstance } from './PresetSelectionModel';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { computed, signal } from '@preact/signals-react';
import type { ConcreteIndicator } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { SelfAliasIndicatorFormula } from '@thinkalpha/platform-ws-client/contracts/ideas/conditions.js';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { FormulaService } from 'src/services/FormulaService';

export interface FlagConditionInit {
    id: string;
    enabled: boolean;
    indicator: ConcreteIndicator;
    invert?: boolean;
    selection?: SelfAliasIndicatorFormula;
    searchModel: UniverseSearchModel;
    formulaService: FormulaService;
}

interface FlagConditionState {
    readonly enabled: boolean;
    readonly indicator: ConcreteIndicator;
    readonly invert: boolean | undefined;
}

type FlagConditionAction =
    | { type: 'SET_ENABLED'; enabled: boolean }
    | { type: 'SET_INVERT'; invert: boolean | undefined };

function reducer(state: FlagConditionState, action: FlagConditionAction): FlagConditionState {
    switch (action.type) {
        case 'SET_ENABLED':
            if (state.enabled === action.enabled) return state;
            return { ...state, enabled: action.enabled };
        case 'SET_INVERT':
            if (state.invert === action.invert) return state;
            return { ...state, invert: action.invert };
        default:
            throw new UnreachableCaseError(action);
    }
}

/**
 * Boolean flag criterion: keeps symbols for which the `indicator`
 * evaluates to `true` (or `false` when `invert` is set). There is no
 * per-value picker — membership is the indicator itself.
 *
 * Use for indicators of boolean data type (e.g. "Is Listed", "Is ETF").
 * Toggle direction via `setInvert`; `setEnabled` controls participation
 * in the overall filter without losing the indicator binding.
 *
 * The current preset selection lives on `presetSelection`; the `selection`
 * signal exposed here is a thin computed projection used for wire
 * serialization.
 */
export class FlagConditionModel implements Disposable {
    readonly type = 'flag' as const;

    readonly #state: Signal<FlagConditionState>;

    readonly id: string;
    readonly enabled: ReadonlySignal<boolean>;
    readonly indicator: ReadonlySignal<ConcreteIndicator>;
    readonly invert: ReadonlySignal<boolean | undefined>;
    readonly selection: ReadonlySignal<SelfAliasIndicatorFormula | undefined>;
    readonly presetSelection: PresetSelectionModelInstance;
    readonly validationErrors: ReadonlySignal<string[]>;
    readonly isValid: ReadonlySignal<boolean>;

    constructor(init: FlagConditionInit) {
        this.id = init.id;
        this.presetSelection = new PresetSelectionModel({
            indicator: init.indicator,
            initialSelection: init.selection,
            searchModel: init.searchModel,
            formulaService: init.formulaService,
        });
        this.#state = signal<FlagConditionState>({
            enabled: init.enabled,
            indicator: init.indicator,
            invert: init.invert,
        });

        this.enabled = computed(() => this.#state.value.enabled);
        this.indicator = computed(() => this.#state.value.indicator);
        this.invert = computed(() => this.#state.value.invert);
        this.selection = computed(() => this.presetSelection.selectedFormula.value);
        this.validationErrors = computed((): string[] => []);
        this.isValid = computed(() => true);
    }

    #dispatch(action: FlagConditionAction): void {
        this.#state.value = reducer(this.#state.value, action);
    }

    setEnabled(enabled: boolean): void {
        this.#dispatch({ type: 'SET_ENABLED', enabled });
    }

    setInvert(invert: boolean | undefined): void {
        this.#dispatch({ type: 'SET_INVERT', invert });
    }

    [Symbol.dispose](): void {
        this.presetSelection[Symbol.dispose]();
    }
}
